import { useEffect, useMemo, useRef, useState } from 'react'

import { LogoDigitizationSetting } from '../../../../../../__generated__/graphql/catalog/graphql'
import { reportClientError } from '../../../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { PlacementId } from '../../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { useEmbroideryAvailability } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryAvailabilityProvider'
import { useSavedEmbroideryPreferences } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryPreferencesProvider'
import { useEmbroideryPrices } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryPricingProvider'
import { useEmbroiderySelection } from '../../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import { EmbroideryItemOfType } from '../../../../../../containers/EmbroideryContainer/providers/types'
import { LogoFile } from '../../../../../../containers/EmbroideryContainer/types'
import useEmbroideryLogoUpload, {
  logoUploadErrorContext,
} from '../../../../../../containers/EmbroideryContainer/useEmbroideryLogoUpload'
import { useNewLogoInOrder } from '../../../../../../containers/EmbroideryContainer/useNewLogoInOrder'
import { reportSelectionRefused } from '../../../../../../containers/EmbroideryContainer/utils/reportEmbroideryError'
import { FOCUSABLE } from '../../../../../../lib/utils/focusable'
import { PanelBody } from '../../../styles'
import { FormHeader } from '../../FormHeader/FormHeader'
import { ModalFooter } from '../../ModalFooter/ModalFooter'
import { PlacementOccupants } from '../../PlacementOccupants/PlacementOccupants'
import { PlacementPicker } from '../../PlacementPicker/PlacementPicker'
import { useShowDraftInPreview } from '../../preview/PreviewDraftContext'
import { deriveLogoEditorStep, deriveSelectedLogo, logoChoices } from '../LogoPicker/logoChoices'

import { LogoDigitizationStep, LogoDraft, LogoDraftErrors } from './LogoDigitizationStep'
import { LogoSummaryStep } from './LogoSummaryStep'

const EMPTY_DRAFT: LogoDraft = {
  file: undefined,
  name: '',
  note: '',
  digitizationSetting: undefined,
  rightsAccepted: false,
}

const needsSetup = (logoFile: LogoFile) => logoFile.status !== 'APPROVED'

export type LogoPlacementEditorProps = {
  onDone(): void
  onBack(): void
}

/**
 * The logo editor: one placement, and one logo with the digitization request a new logo needs.
 *
 * It runs in two steps under one heading. The digitization step collects what the digitizer needs
 * and ends in `NEXT`, which uploads the artwork and chooses the logo. Nothing reaches the selection
 * until "Update garment".
 */
export const LogoPlacementEditor: React.FC<LogoPlacementEditorProps> = ({ onDone, onBack }) => {
  const { items, saveItemsOfType } = useEmbroiderySelection()
  const { placementsForType } = useEmbroideryAvailability()
  const { formatPrice, getPriceInCents } = useEmbroideryPrices()
  const uploadLogo = useEmbroideryLogoUpload()
  const {
    embroidery: {
      modal: {
        backButton: backLabel,
        forms: {
          dtcLogoEmbroideryForm: logoCopy,
          logoFields,
          submissionButtons,
          titles,
          validationErrorMessages,
        },
      },
    },
  } = useTranslation()

  const logoItem = items.find(
    (item): item is EmbroideryItemOfType<'logo'> => item.typeKey === 'logo'
  )

  const { userLogos, logosStatus } = useSavedEmbroideryPreferences()
  const newLogoInOrder = useNewLogoInOrder()?.logoFile
  const canUploadNewLogo = newLogoInOrder === undefined

  // A new logo starts on the first placement still open to it, in the order the picker lists them.
  const [draftPlacementId, setDraftPlacementId] = useState<PlacementId | undefined>(
    () => logoItem?.placementId ?? placementsForType('logo')[0]?.id
  )
  // Whether the customer picked the placement, rather than it being the one the editor started on.
  const [hasPickedPlacement, setHasPickedPlacement] = useState(false)
  // The artwork behind `uploadedLogo`, so editing it doesn't upload it again.
  const [uploadedFile, setUploadedFile] = useState<File | undefined>(() =>
    logoItem?.values.requiresSetup && logoItem.values.logoFile
      ? standInFileFor(logoItem.values.logoFile)
      : undefined
  )
  const [draft, setDraft] = useState<LogoDraft>(() => draftFrom(logoItem, uploadedFile))
  // This garment's own upload, kept on offer while another logo is chosen.
  const [uploadedLogo, setUploadedLogo] = useState<LogoFile | undefined>(() =>
    logoItem?.values.requiresSetup ? logoItem.values.logoFile : undefined
  )
  /**
   * The logo on the garment, or the one chosen or uploaded here since. Kept apart from the selection
   * until "Update garment", so leaving the editor any other way leaves the garment as it was.
   */
  const [chosenLogo, setChosenLogo] = useState<LogoFile | undefined>(logoItem?.values.logoFile)
  const [hasChosenLogo, setHasChosenLogo] = useState(false)
  const [isAddingNewLogo, setIsAddingNewLogo] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [placementError, setPlacementError] = useState<string | undefined>()
  const [draftErrors, setDraftErrors] = useState<LogoDraftErrors>({})

  const choices = logoChoices({ newLogoInOrder, uploadedLogo, chosenLogo, userLogos })
  const selectedLogo = deriveSelectedLogo({
    chosenLogo,
    choices,
    isLoadingChoices: logosStatus === 'pending',
    hasPlacement: hasPickedPlacement,
  })
  const step = deriveLogoEditorStep({
    isAddingNewLogo,
    canUploadNewLogo,
    hasChoices: choices.length > 0,
    isLoadingChoices: logosStatus === 'pending',
  })
  const isSummary = step === 'summary'

  // Changing steps unmounts the control the customer used, so focus moves to the new step's first control.
  const panelRef = useRef<HTMLDivElement>(null)
  const shouldFocusStep = useRef(false)
  const previousStep = useRef(step)
  // Runs every render so a request that didn't change the step is cleared, not left for a later change.
  useEffect(() => {
    const stepChanged = previousStep.current !== step
    previousStep.current = step
    if (!shouldFocusStep.current) return
    shouldFocusStep.current = false
    if (!stepChanged) return
    panelRef.current?.querySelector<HTMLElement>(`[data-logo-step] :is(${FOCUSABLE})`)?.focus()
  })
  const setupPriceLabel = formatPrice(getPriceInCents('logo_setup'), true)

  const isUploadedArtwork = draft.file !== undefined && draft.file === uploadedFile
  const draftFileUrl = useObjectUrl(isUploadedArtwork ? undefined : draft.file)
  const draftPreview = isUploadedArtwork ? uploadedLogo?.preview : draftFileUrl
  const uploadedPreview = selectedLogo?.preview
  const imageSrc = isSummary ? uploadedPreview : draftPreview ?? uploadedPreview
  const digitizationSetting = isSummary
    ? selectedLogo?.digitizationSetting
    : draft.digitizationSetting
  // What "Update garment" would save, for the summary to price and describe. It takes the logo the
  // picker shows as selected, so the starting placement and the only new logo are one click to add.
  const draftItem: EmbroideryItemOfType<'logo'> | undefined =
    selectedLogo && draftPlacementId
      ? {
          instanceId: logoItem?.instanceId ?? 'draft-logo',
          typeKey: 'logo',
          placementId: draftPlacementId,
          values: { logoFile: selectedLogo, requiresSetup: needsSetup(selectedLogo) },
          source: logoItem?.source ?? 'USER',
          enabledForBag: logoItem?.enabledForBag ?? true,
        }
      : undefined
  useShowDraftInPreview({
    typeKey: 'logo',
    marks:
      draftPlacementId === undefined
        ? []
        : [
            {
              key: logoItem?.instanceId ?? 'draft-logo',
              placementId: draftPlacementId,
              typeKey: 'logo',
              values: {
                imageSrc,
                isBlackAndWhite: digitizationSetting === 'BLACK_WHITE',
              },
            },
          ],
    // The close-up waits for a logo picked or chosen here; the saved one alone leaves the garment up.
    focusedPlacementId: draft.file || hasChosenLogo ? draftPlacementId : undefined,
  })

  const selectPlacement = (placementId: PlacementId) => {
    setDraftPlacementId(placementId)
    setHasPickedPlacement(true)
    setPlacementError(undefined)
    // Picking a placement takes the logo the picker shows as selected, so it's one click to add.
    if (!chosenLogo && selectedLogo) setChosenLogo(selectedLogo)
  }

  const next = async () => {
    const errors: LogoDraftErrors = {
      file: draft.file === undefined ? validationErrorMessages.requiredFile : undefined,
      name: draft.name.trim() === '' ? validationErrorMessages.requiredLogoName : undefined,
      color:
        draft.digitizationSetting === undefined
          ? validationErrorMessages.requiredDigitizationSetting
          : undefined,
      rights: draft.rightsAccepted ? undefined : validationErrorMessages.requiredTos,
    }
    const missingPlacement = draftPlacementId === undefined

    setPlacementError(missingPlacement ? validationErrorMessages.requiredLogoPlacement : undefined)
    setDraftErrors(errors)
    if (missingPlacement || Object.values(errors).some(Boolean)) return

    setIsUploading(true)
    try {
      const logoFile =
        isUploadedArtwork && uploadedLogo
          ? {
              ...uploadedLogo,
              name: draft.name,
              notes: draft.note,
              digitizationSetting: draft.digitizationSetting!,
            }
          : {
              ...(await uploadLogo({
                file: draft.file!,
                name: draft.name,
                digitization_setting: draft.digitizationSetting!,
                notes: draft.note,
              })),
              pickedFile: { name: draft.file!.name, type: draft.file!.type },
            }
      shouldFocusStep.current = true
      setUploadedFile(draft.file)
      setUploadedLogo(logoFile)
      setChosenLogo(logoFile)
      setIsAddingNewLogo(false)
    } catch (error) {
      reportClientError({
        error: error as Error,
        context: {
          scope: 'LogoPlacementEditor',
          category: 'embroidery',
          message: 'embroidery logo upload request failed',
          level: 'error',
          ...logoUploadErrorContext(error),
        },
      })
      setDraftErrors({ file: validationErrorMessages.fileUploadError })
    } finally {
      setIsUploading(false)
    }
  }

  const chooseLogo = (logoFile: LogoFile) => {
    setHasChosenLogo(true)
    setChosenLogo(logoFile)
    if (draftPlacementId === undefined) {
      setPlacementError(validationErrorMessages.requiredLogoPlacement)
    }
  }

  const addNewLogo = () => {
    setDraft(EMPTY_DRAFT)
    setDraftErrors({})
    shouldFocusStep.current = true
    setIsAddingNewLogo(true)
  }

  // An emptied editor saves by taking the logo off the garment, so it needs a logo there to take.
  const isEmpty = draftPlacementId === undefined && chosenLogo === undefined

  const save = () => {
    if (isEmpty && logoItem) {
      const removed = saveItemsOfType('logo', [])
      if (removed.ok) onDone()
      else reportSelectionRefused(removed.reason, 'logo', [])
      return
    }
    if (!draftItem) return

    // A refusal means the engine rejected a placement the picker should have disabled, so there
    // is no message for it — the card the customer picked is the one to go back to.
    const saved = saveItemsOfType('logo', [
      {
        instanceId: logoItem?.instanceId,
        placementId: draftItem.placementId,
        values: draftItem.values,
      },
    ])
    if (saved.ok) onDone()
    else reportSelectionRefused(saved.reason, 'logo', [draftItem.placementId])
  }

  /** Empties the editor, for "Update garment" to take the logo off the garment. */
  const reset = () => {
    shouldFocusStep.current = true
    setDraftPlacementId(undefined)
    setHasPickedPlacement(false)
    setDraft(EMPTY_DRAFT)
    setUploadedFile(undefined)
    setUploadedLogo(undefined)
    setChosenLogo(undefined)
    setHasChosenLogo(false)
    setIsAddingNewLogo(false)
    setPlacementError(undefined)
    setDraftErrors({})
  }

  return (
    <>
      <PanelBody ref={panelRef} $fillPanel={false}>
        <FormHeader title={titles.logo} onBack={onBack} backLabel={backLabel} />

        <PlacementPicker
          mode='single'
          typeKey='logo'
          label={logoFields.positionLabel}
          instructions={logoFields.placementInstructions}
          errorMessage={placementError}
          editingInstanceId={logoItem?.instanceId}
          selectedPlacementId={draftPlacementId}
          onSelectPlacement={selectPlacement}
        />

        <PlacementOccupants typeKey='logo' placementId={draftPlacementId} dividers='before' />

        {isSummary && (
          <LogoSummaryStep
            item={draftItem}
            isNewLogoTaken={!canUploadNewLogo}
            picker={{
              choices,
              selectedLogoId: selectedLogo?.id,
              onChooseLogo: chooseLogo,
              editableLogoId: canUploadNewLogo ? uploadedLogo?.id : undefined,
              onEditLogo: () => {
                shouldFocusStep.current = true
                setIsAddingNewLogo(true)
              },
              canAddNewLogo: canUploadNewLogo && uploadedLogo === undefined,
              setupPriceLabel,
              onAddNewLogo: addNewLogo,
            }}
          />
        )}
        {step === 'digitization' && (
          <LogoDigitizationStep
            draft={draft}
            onChangeDraft={setDraft}
            errors={draftErrors}
            setupPriceLabel={setupPriceLabel}
            filePreviewUrl={isUploadedArtwork ? uploadedLogo?.preview : undefined}
          />
        )}

        {/* TODO: Enable when preferences are ready in multiplacement */}
        {/* <SavePreferenceToggle
          typeKey='logo'
          checked={savePreference}
          onToggle={() => setSavePreference(current => !current)}
        /> */}
      </PanelBody>

      {isSummary && (
        <ModalFooter
          primaryLabel={submissionButtons.updateGarmentCta}
          onPrimary={save}
          primaryDisabled={!draftItem && !(isEmpty && logoItem)}
          secondaryLabel={submissionButtons.resetCta}
          onSecondary={reset}
          sticky={false}
        />
      )}
      {step === 'digitization' && draft.file && (
        <ModalFooter
          primaryLabel={logoCopy.nextCta}
          onPrimary={() => void next()}
          primaryDisabled={isUploading}
          sticky={false}
        />
      )}
    </>
  )
}

/**
 * Seeds the form from a logo already in the selection, so re-editing one starts where the
 * customer left it.
 */
function draftFrom(
  logoItem: EmbroideryItemOfType<'logo'> | undefined,
  uploadedFile: File | undefined
): LogoDraft {
  const logoFile = logoItem?.values.logoFile
  if (!logoFile) return EMPTY_DRAFT

  return {
    file: uploadedFile,
    name: logoFile.name,
    note: logoFile.notes ?? '',
    digitizationSetting: logoFile.digitizationSetting as LogoDigitizationSetting,
    rightsAccepted: true,
  }
}

/** Stands in for an earlier visit's upload, whose picked file is gone, so its form can reopen. */
function standInFileFor(logoFile: LogoFile): File {
  const { name, type } = logoFile.pickedFile ?? {
    name: logoFile.name,
    // Without the picked file, show the logo's preview, which is always an image.
    type: 'image/png',
  }
  return new File([], name, { type })
}

/** A local URL for a picked file, so it can be shown before it is uploaded. */
function useObjectUrl(file: File | undefined): string | undefined {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file])
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url)
    },
    [url]
  )
  return url
}
